// User Service
// Handles storage and retrieval of users for authentication and client management

export interface User {
  id: string;
  email: string;
  phone: string;
  password: string; // In production, this should be hashed
  firstName: string;
  lastName: string;
  registeredAt: string;
  lastLogin?: string;
  role: 'client' | 'manager';
  status: 'active' | 'inactive';
}

const STORAGE_KEY = 'camping_users';

// Get all users from localStorage
export const getAllUsers = (): User[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Error reading users from storage:', error);
    return [];
  }
};

// Save users to localStorage
export const saveUsers = (users: User[]): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(users));
  } catch (error) {
    console.error('Error saving users to storage:', error);
  }
};

// Create a new user
export const createUser = (userData: Omit<User, 'id' | 'registeredAt' | 'lastLogin'>): User => {
  const users = getAllUsers();
  
  // Check if user already exists
  if (users.find(u => u.email === userData.email)) {
    throw new Error('User with this email already exists');
  }
  
  const newUser: User = {
    ...userData,
    id: `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    registeredAt: new Date().toISOString(),
    lastLogin: undefined,
    status: 'active'
  };
  
  users.push(newUser);
  saveUsers(users);
  
  // Dispatch custom event to notify other components
  window.dispatchEvent(new CustomEvent('userCreated', { detail: newUser }));
  
  return newUser;
};

// Authenticate user by email and password
export const authenticateUser = (email: string, password: string): User | null => {
  const users = getAllUsers();
  console.log('All users in storage:', users.map(u => ({ email: u.email, status: u.status })));
  
  // Normalize email for comparison (trim and lowercase)
  const normalizedEmail = email.trim().toLowerCase();
  
  const user = users.find(u => 
    u.email.trim().toLowerCase() === normalizedEmail && 
    u.password === password && 
    u.status === 'active'
  );
  
  if (user) {
    console.log('User found:', { email: user.email, firstName: user.firstName });
    // Update last login
    user.lastLogin = new Date().toISOString();
    const updatedUsers = users.map(u => u.id === user.id ? user : u);
    saveUsers(updatedUsers);
    
    // Dispatch custom event
    window.dispatchEvent(new CustomEvent('userLoggedIn', { detail: user }));
    
    return user;
  }
  
  console.log('User not found or credentials invalid');
  return null;
};

// Get user by ID
export const getUserById = (id: string): User | null => {
  const users = getAllUsers();
  return users.find(u => u.id === id) || null;
};

// Get user by email
export const getUserByEmail = (email: string): User | null => {
  const users = getAllUsers();
  return users.find(u => u.email === email) || null;
};

// Update user
export const updateUser = (userId: string, updates: Partial<User>): boolean => {
  const users = getAllUsers();
  const index = users.findIndex(u => u.id === userId);
  
  if (index === -1) return false;
  
  users[index] = { ...users[index], ...updates };
  saveUsers(users);
  
  // Dispatch custom event
  window.dispatchEvent(new CustomEvent('userUpdated', { detail: users[index] }));
  
  return true;
};

// Delete user
export const deleteUser = (userId: string): boolean => {
  const users = getAllUsers();
  const filtered = users.filter(u => u.id !== userId);
  
  if (filtered.length === users.length) return false;
  
  saveUsers(filtered);
  
  // Dispatch custom event
  window.dispatchEvent(new CustomEvent('userDeleted', { detail: userId }));
  
  return true;
};

// Get all client users (non-manager)
export const getAllClients = (): User[] => {
  return getAllUsers().filter(u => u.role === 'client');
};

// Get all manager users
export const getAllManagers = (): User[] => {
  return getAllUsers().filter(u => u.role === 'manager');
};

